import { apiClient } from '@/lib/api-client';
import type {
  AdoptionStatus,
  BusinessUnit,
  Company,
  CreateBusinessUnitInput,
  ImportableMember,
  OnboardResult,
  OnboardSubsidiaryInput,
  RevokeMemberInput,
  SetUnitManagerInput,
  UnitMember,
  UpdateBusinessUnitInput,
} from '../types';

const BASE = '/crm/business-units';

/** BU-v2 Phase 3 (P3-2): operating-unit management — crm_admin surface. */
export const businessUnitsApi = {
  list: async (): Promise<BusinessUnit[]> => {
    return apiClient.get<BusinessUnit[]>(BASE);
  },

  // P3-3b: the caller's own reachable units — switcher source for every CRM role.
  myUnits: async (): Promise<Pick<BusinessUnit, 'id' | 'name' | 'isDefault' | 'isActive'>[]> => {
    return apiClient.get<Pick<BusinessUnit, 'id' | 'name' | 'isDefault' | 'isActive'>[]>(
      `${BASE}/my-units`,
    );
  },

  create: async (input: CreateBusinessUnitInput): Promise<BusinessUnit> => {
    return apiClient.post<BusinessUnit>(BASE, input);
  },

  update: async (id: string, input: UpdateBusinessUnitInput): Promise<BusinessUnit> => {
    return apiClient.patch<BusinessUnit>(`${BASE}/${id}`, input);
  },

  onboard: async (input: OnboardSubsidiaryInput): Promise<OnboardResult> => {
    return apiClient.post<OnboardResult>(`${BASE}/onboard`, input);
  },

  listMembers: async (unitId: string): Promise<UnitMember[]> => {
    return apiClient.get<UnitMember[]>(`${BASE}/${unitId}/members`);
  },

  addMember: async (
    unitId: string,
    employeeId: string,
    secondmentReason?: string,
  ): Promise<unknown> => {
    return apiClient.post(`${BASE}/${unitId}/members`, {
      employeeId,
      ...(secondmentReason && { secondmentReason }),
    });
  },

  revokeMember: async (unitId: string, input: RevokeMemberInput): Promise<unknown> => {
    return apiClient.post(`${BASE}/${unitId}/members/revoke`, input);
  },

  // Slice B: set or clear the unit manager (auto-adds them as a member).
  setManager: async (unitId: string, input: SetUnitManagerInput): Promise<BusinessUnit> => {
    return apiClient.patch<BusinessUnit>(`${BASE}/${unitId}/manager`, input);
  },

  listCompanies: async (): Promise<Company[]> => {
    return apiClient.get<Company[]>(`${BASE}/companies`);
  },

  listImportableMembers: async (
    unitId: string,
    legalEntityId: string,
  ): Promise<ImportableMember[]> => {
    return apiClient.get<ImportableMember[]>(
      `${BASE}/${unitId}/importable-members?legalEntityId=${legalEntityId}`,
    );
  },

  importMembers: async (unitId: string, employeeIds: string[]): Promise<{ imported: number }> => {
    return apiClient.post<{ imported: number }>(`${BASE}/${unitId}/members/import`, {
      employeeIds,
    });
  },

  // BU-v2 A4: adoption is a resumable background job — start it, then poll status.
  startAdoption: async (targetUnitId: string): Promise<AdoptionStatus> => {
    return apiClient.post<AdoptionStatus>(`${BASE}/adoption/start`, { targetUnitId });
  },

  declineAdoption: async (reason?: string): Promise<{ defaultMembersGranted: number }> => {
    return apiClient.post<{ defaultMembersGranted: number }>(`${BASE}/adoption/decline`, {
      reason,
    });
  },

  adoptionStatus: async (): Promise<AdoptionStatus> => {
    return apiClient.get<AdoptionStatus>(`${BASE}/adoption/status`);
  },
};
